import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';

import {
	deleteAmaConversation,
	listAmaConversations,
	renameAmaConversation,
} from '../../services/api';

const ConversationList = ({ currentId, onSelect, onNew, refreshKey }) => {
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(false);
	const [renaming, setRenaming] = useState(null);
	const [newTitle, setNewTitle] = useState('');
	const [confirmDelete, setConfirmDelete] = useState(null);

	const reload = async () => {
		setLoading(true);
		try {
			const data = await listAmaConversations();
			setItems(data.results || []);
		} catch (e) {
			setItems([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		reload();
	}, [refreshKey]);

	const handleRename = async () => {
		if (!renaming || !newTitle.trim()) return;
		try {
			await renameAmaConversation(renaming.id, newTitle.trim());
			await reload();
		} finally {
			setRenaming(null);
			setNewTitle('');
		}
	};

	const handleDelete = async () => {
		if (!confirmDelete) return;
		const deletedId = confirmDelete.id;
		try {
			await deleteAmaConversation(deletedId);
			await reload();
			if (currentId === deletedId) onNew();
		} finally {
			setConfirmDelete(null);
		}
	};

	return (
		<Box sx={{ width: 260, display: 'flex', flexDirection: 'column', borderRight: '1px solid', borderColor: 'divider', height: '100%' }}>
			<Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
				<Button fullWidth variant="outlined" startIcon={<AddIcon />} onClick={onNew} size="small">
					New chat
				</Button>
			</Box>
			<Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
				{items.length === 0 && !loading && (
					<Typography variant="caption" sx={{ p: 2, display: 'block', color: 'text.secondary' }}>
						No conversations yet.
					</Typography>
				)}
				<List dense disablePadding>
					{items.map((c) => (
						<ListItemButton
							key={c.id}
							selected={currentId === c.id}
							onClick={() => onSelect(c.id)}
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 0.5,
								pr: 0.5,
								'& .actions': { opacity: 0, transition: 'opacity 0.15s' },
								'&:hover .actions': { opacity: 1 },
							}}
						>
							<Box sx={{ flexGrow: 1, minWidth: 0 }}>
								<Typography variant="body2" noWrap title={c.title}>
									{c.title || 'Untitled'}
								</Typography>
							</Box>
							<Box className="actions" sx={{ display: 'flex' }}>
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										setRenaming(c);
										setNewTitle(c.title || '');
									}}
								>
									<EditIcon fontSize="inherit" />
								</IconButton>
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										setConfirmDelete(c);
									}}
								>
									<DeleteOutlineIcon fontSize="inherit" />
								</IconButton>
							</Box>
						</ListItemButton>
					))}
				</List>
			</Box>

			<Dialog open={!!renaming} onClose={() => setRenaming(null)} maxWidth="xs" fullWidth>
				<DialogTitle>Rename conversation</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						fullWidth
						size="small"
						value={newTitle}
						onChange={(e) => setNewTitle(e.target.value)}
						onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); }}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRenaming(null)}>Cancel</Button>
					<Button onClick={handleRename} disabled={!newTitle.trim()} variant="contained">Save</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
				<DialogTitle>Delete conversation?</DialogTitle>
				<DialogContent>
					<Typography variant="body2">
						"{confirmDelete?.title || 'Untitled'}" will be permanently deleted along with all its messages.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
					<Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default ConversationList;
